import { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { canonicalTeamName } from "../utils/feeRules";

export const getTeamRealName = (teamName, realNameByTeam = {}) =>
  realNameByTeam[canonicalTeamName(teamName)] || "";

export const formatTeamWithRealName = (teamName, realNameByTeam = {}) => {
  const team = canonicalTeamName(teamName) || "ไม่ระบุ";
  const realName = getTeamRealName(team, realNameByTeam);
  return realName ? `${team} · ${realName}` : team;
};

export default function useTeamDirectory() {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let unsubscribeDirectory = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (unsubscribeDirectory) {
        unsubscribeDirectory();
        unsubscribeDirectory = null;
      }

      if (!currentUser) {
        setUsers([]);
        return;
      }

      // อ่านโปรไฟล์ตัวเองก่อน แล้วค่อยตัดสินใจว่าจะอ่าน directory ทั้งหมดหรือไม่
      // Team อ่านได้เฉพาะ document ของตัวเอง เพื่อลดการเปิดเผยอีเมล/ข้อมูลผู้ใช้คนอื่นใน browser
      const selfRef = doc(db, "users", currentUser.uid);
      unsubscribeDirectory = onSnapshot(
        selfRef,
        (selfSnap) => {
          if (unsubscribeDirectory && unsubscribeDirectory.__allUsersListener) return;
          const self = selfSnap.exists() ? { id: selfSnap.id, ...selfSnap.data() } : null;
          const role = self?.role || "pending";

          if (role === "admin" || role === "report") {
            if (unsubscribeDirectory) unsubscribeDirectory();
            const allUsersUnsub = onSnapshot(
              collection(db, "users"),
              (snapshot) => {
                setUsers(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
              },
              (error) => console.error("โหลดรายชื่อจริงของทีมไม่สำเร็จ", error)
            );
            allUsersUnsub.__allUsersListener = true;
            unsubscribeDirectory = allUsersUnsub;
          } else {
            setUsers(self ? [self] : []);
          }
        },
        (error) => {
          console.error("โหลดข้อมูลผู้ใช้ไม่สำเร็จ", error);
          setUsers([]);
        }
      );
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeDirectory) unsubscribeDirectory();
    };
  }, []);

  const realNameByTeam = useMemo(() => {
    const map = {};
    users.forEach((user) => {
      const team = canonicalTeamName(user.teamName);
      const realName = String(user.realName || "").trim();
      if (team && realName && !map[team]) map[team] = realName;
    });
    return map;
  }, [users]);

  return { users, realNameByTeam };
}
